import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Waypoint } from "react-waypoint";

import { ErrorRetry } from "@/features/common/components/ErrorRetry";
import { Checkbox } from "@/features/common/components/form/Checkbox";
import { ModelInvitationDialogViewProps } from "@/features/job/components/manage/ModelInvitationDialogView";
import { InviteModelInputs } from "@/features/job/components/manage/ModelInvitationDialogView/InviteModelForm";
import { EmptyModelList } from "@/features/model/components/EmptyModelList";
import { ModelInfoItem } from "@/features/model/components/ModelInfoItem";
import { ModelInfoListSkeleton } from "@/features/model/components/ModelInfoListSkeleton";
import {
  GenderEnum,
  QueryMode,
  SortOrder,
  useInfiniteModelsQuery,
} from "@/generated/graphql-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";

type InviteModelListProps = {
  count?: number;
  searchInput?: string;
  gender?: GenderEnum;
} & ModelInvitationDialogViewProps;

const {
  features: {
    table: { itemsPerQuery },
  },
} = getPublicConfig();

export const InviteModelList = ({
  count = 0,
  job,
  searchInput = "",
  gender,
}: Omit<InviteModelListProps, "invitationsCount">) => {
  const { control, register } = useFormContext<InviteModelInputs>();
  const modelUserIds = useWatch({
    control,
    name: "modelUserIds",
    defaultValue: [],
  });
  const {
    data: { pages } = {},
    isLoading,
    isFetching,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
  } = useInfiniteModelsQuery(
    "where",
    {
      where: {
        gender: { equals: gender },
        jobApplications: {
          none: {
            jobId: { equals: job.id },
          },
        },
        username: {
          contains: searchInput,
          mode: QueryMode.Insensitive,
        },
      },
      orderBy: [{ order: SortOrder.Desc }, { id: SortOrder.Desc }],
      take: itemsPerQuery,
    },
    {
      getNextPageParam: (lastPage, pages) => {
        // Not sure why page.models is possibly undefined at runtime
        const fetchedCount = pages.flatMap((page) => page?.models ?? []).length;

        if (
          fetchedCount % itemsPerQuery === 0 &&
          fetchedCount !== 0 &&
          fetchedCount < count
        ) {
          return { cursor: lastPage.models?.[lastPage.models.length - 1]?.id };
        }

        return false;
      },
    },
  );

  const models = useMemo(
    () => pages?.flatMap((page) => page.models) ?? [],
    [pages],
  );

  if (isLoading) {
    return (
      <div className="ml-7 space-y-4 p-2">
        <ModelInfoListSkeleton />
      </div>
    );
  }

  if (error) {
    return <ErrorRetry refetch={refetch} error={error}/>;
  }

  if (models.length === 0) {
    return <EmptyModelList title="KOL" />;
  }

  return (
    <>
      {models.map((model) => (
        <Checkbox
          {...register("modelUserIds")}
          label={<ModelInfoItem model={model} />}
          key={model.id}
          value={model.userId}
          checked={modelUserIds.includes(model.userId)}
        />
      ))}

      {/* if only one checkbox is rendered, the form value will be in string type but not array of string */}
      {models.length === 1 && (
        <Checkbox {...register("modelUserIds")} label="" className="hidden" />
      )}
      {!isLoading && !!isFetching && (
        <div className="ml-8 space-y-4">
          <ModelInfoListSkeleton />
        </div>
      )}
      <Waypoint
        onEnter={() => {
          if (hasNextPage) fetchNextPage();
        }}
      />
    </>
  );
};
